import isConsumer from "owa-session-store/lib/utils/isConsumer";
import { isAccountFeatureEnabled } from "owa-feature-flags";
import { logGreyError, logUsage } from "owa-analytics";
import fetchPoliciesAndSetStore from "./fetchPoliciesAndSetStore";
import type { AccountSource } from "owa-account-source-list-store";
import { getCoprincipalAccountSourcesByBootState, BootState, onCoprincipalBootStateChanged, onPreCoprincipalAccountRemoved, getGlobalSettingsAccountMailboxInfo, tryGetGlobalSettingsAccountMailboxInfo, } from "owa-account-source-list-store";
import type { MailboxInfo } from "owa-client-types";
import { orchestrator } from "satcheljs";
import { setOcpsPolicyStoreState } from "../mutators/setOcpsPolicyStoreState";
import { OcpsStoreState } from "../store/schema/OcpsPolicyStore";
import getOcpsPolicyStoreState from "../selectors/getOcpsPolicyStoreState";
import { onAccountRemoved } from "./onAccountRemoved";
import { logUsageFromAccounts } from "owa-account-analytics";

export default async function initializeOcpsPolicyStore(origin: string): Promise<void> {
    if (!isAccountFeatureEnabled("ocps-policy-check", getGlobalSettingsAccountMailboxInfo())) {
        return;
    }
    const ocpsPolicyStoreState = getOcpsPolicyStoreState();
    
    if (ocpsPolicyStoreState !== OcpsStoreState.Fetching) {
        logUsageFromAccounts("InitializeOcpsPolicyStore", tryGetGlobalSettingsAccountMailboxInfo(), {
            initSource: origin,
        });
        try {
            setOcpsPolicyStoreState(OcpsStoreState.Fetching);
            const userMailboxes: MailboxInfo[] = getCoprincipalAccountSourcesByBootState(BootState.StartupComplete).map(accountSource => accountSource.mailboxInfo);
            
            for (let i = 0; i < userMailboxes.length; i++) {
                await fetchUserOcpsPolicies(userMailboxes[i], origin);
            }
            
            // Register for boot state change and fetch policies when a new account is added
            /* eslint-disable-next-line owa-custom-rules/invoke-only-in-module-scope -- (https://aka.ms/OWALintWiki)
             * Move this function to the module scope or wrapped it on a once function
             *	> Function should only be invoked in module scope */
            orchestrator(onCoprincipalBootStateChanged, async (arg: {
                updatedAccount: AccountSource;
                newBootState: BootState;
            }) => {
                const actualAccount = arg.updatedAccount;
                const actualBootState = arg.newBootState;
                if (actualBootState === BootState.StartupComplete) {
                    logUsage("FetchPoliciesAndSetStore_AccountAdded", getGlobalSettingsAccountMailboxInfo(), {
                        initSource: origin,
                    });
                    await fetchUserOcpsPolicies(actualAccount.mailboxInfo, origin);
                }
            });
            
            /* eslint-disable-next-line owa-custom-rules/invoke-only-in-module-scope -- (https://aka.ms/OWALintWiki)
             * Move this function to the module scope or wrapped it on a once function
             *	> Function should only be invoked in module scope */
            orchestrator(onPreCoprincipalAccountRemoved, actionMessage => {
                onAccountRemoved(actionMessage.removedAccount.mailboxInfo);
            });
            
            setOcpsPolicyStoreState(OcpsStoreState.Ready);
        }
        catch (e) {
            logGreyError("InitializeOcpsPolicyStore_Failed", getGlobalSettingsAccountMailboxInfo(), e instanceof Error ? e : undefined, {
                initSource: origin,
            });
            throw e;
        }
    }
}

async function fetchUserOcpsPolicies(mailboxInfo: MailboxInfo, origin: string) {
    if (isAccountFeatureEnabled("ocps-policy-check", mailboxInfo) &&
        !isConsumer(undefined, mailboxInfo)) {
        await fetchPoliciesAndSetStore(mailboxInfo, origin);
    }
}
